import {
	AppstoreOutlined,
	ExpandOutlined,
	TabletOutlined,
} from "@ant-design/icons";
import {
	Card,
	Col,
	Divider,
	Empty,
	Image,
	Row,
	Space,
	Spin,
	Tag,
	Typography,
} from "antd";
import type React from "react";
import type { GetRichMenuTemplatesResponse } from "../../../../shared/types/richmenu.types";
import { getRichmenuTemplateImage } from "../../../shared/richmenuTemplates";

const { Text, Title } = Typography;

interface RichMenuTemplatesPanelProps {
	templates?: GetRichMenuTemplatesResponse;
	loading?: boolean;
}

export const RichMenuTemplatesPanel: React.FC<RichMenuTemplatesPanelProps> = ({
	templates,
	loading,
}) => {
	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "40px 0" }}>
				<Spin size="large" />
				<div style={{ marginTop: 16 }}>
					<Text type="secondary">テンプレートを読み込み中...</Text>
				</div>
			</div>
		);
	}

	if (
		!templates ||
		(!templates.data.compact.length && !templates.data.large.length)
	) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description="利用可能なテンプレートがありません"
			/>
		);
	}

	const TemplateCard: React.FC<{ template: any; isCompact: boolean }> = ({
		template,
		isCompact,
	}) => {
		const templateImage = getRichmenuTemplateImage(template.name);

		return (
			<Card
				style={{
					marginBottom: 8,
					borderRadius: 8,
					overflow: "hidden",
					border: "1px solid #f0f0f0",
					transition: "all 0.3s ease",
				}}
				styles={{
					body: {
						padding: 12,
					},
				}}
				hoverable
			>
				<Space direction="vertical" style={{ width: "100%" }}>
					{/* Template Image */}
					{templateImage && (
						<div style={{ textAlign: "center" }}>
							<Image
								src={templateImage}
								alt={template.name}
								style={{
									width: "100%",
									height: isCompact ? 40 : 60,
									objectFit: "cover",
									borderRadius: 4,
									border: "1px solid #d9d9d9",
								}}
								preview={{
									mask: (
										<div style={{ fontSize: "12px" }}>
											<ExpandOutlined /> プレビュー
										</div>
									),
								}}
							/>
						</div>
					)}

					{/* Template Info */}
					<div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: 4,
							}}
						>
							<Text strong style={{ fontSize: "12px" }}>
								{template.name}
							</Text>
							<Tag
								color={isCompact ? "blue" : "green"}
								icon={isCompact ? <TabletOutlined /> : <AppstoreOutlined />}
							>
								{isCompact ? "Compact" : "Large"}
							</Tag>
						</div>

						<div style={{ marginBottom: 4 }}>
							<Text type="secondary" style={{ fontSize: "11px" }}>
								サイズ: {template.size.width} × {template.size.height}
							</Text>
						</div>

						{template.areas && template.areas.length > 0 && (
							<div>
								<Text type="secondary" style={{ fontSize: "11px" }}>
									アクションエリア: {template.areas.length}個
								</Text>
							</div>
						)}
					</div>
				</Space>
			</Card>
		);
	};

	return (
		<div style={{ padding: "8px 0" }}>
			{/* Compact Templates */}
			{templates.data.compact && templates.data.compact.length > 0 && (
				<div style={{ marginBottom: 24 }}>
					<div
						style={{ marginBottom: 12, display: "flex", alignItems: "center" }}
					>
						<TabletOutlined style={{ color: "#1890ff", marginRight: 8 }} />
						<Title level={5} style={{ margin: 0 }}>
							Compact テンプレート
						</Title>
						<Tag color="blue" style={{ marginLeft: 8 }}>
							{templates.data.compact.length}個
						</Tag>
					</div>

					<Row gutter={[8, 8]}>
						{templates.data.compact.map((template, index) => (
							<Col xs={24} key={`compact-${template.name}-${index}`}>
								<TemplateCard template={template} isCompact={true} />
							</Col>
						))}
					</Row>
				</div>
			)}

			{/* Divider */}
			{templates.data.compact.length > 0 && templates.data.large.length > 0 && (
				<Divider style={{ margin: "16px 0" }} />
			)}

			{/* Large Templates */}
			{templates.data.large && templates.data.large.length > 0 && (
				<div>
					<div
						style={{ marginBottom: 12, display: "flex", alignItems: "center" }}
					>
						<AppstoreOutlined style={{ color: "#52c41a", marginRight: 8 }} />
						<Title level={5} style={{ margin: 0 }}>
							Large テンプレート
						</Title>
						<Tag color="green" style={{ marginLeft: 8 }}>
							{templates.data.large.length}個
						</Tag>
					</div>

					<Row gutter={[8, 8]}>
						{templates.data.large.map((template, index) => (
							<Col xs={24} key={`large-${template.name}-${index}`}>
								<TemplateCard template={template} isCompact={false} />
							</Col>
						))}
					</Row>
				</div>
			)}

			{/* Template Usage Guide */}
			<Card
				style={{
					marginTop: 16,
					background: "linear-gradient(135deg, #f0f2ff 0%, #e6f4ff 100%)",
					border: "1px solid #91caff",
				}}
			>
				<Space direction="vertical">
					<Text strong style={{ fontSize: "12px", color: "#1890ff" }}>
						💡 テンプレートの使い方
					</Text>
					<Text type="secondary" style={{ fontSize: "11px" }}>
						• Compact: 縦に小さいメニュー
					</Text>
					<Text type="secondary" style={{ fontSize: "11px" }}>
						• Large: 縦に大きいメニュー
					</Text>
					<Text type="secondary" style={{ fontSize: "11px" }}>
						タブグループ作成時にテンプレートを選択できます
					</Text>
				</Space>
			</Card>
		</div>
	);
};
